import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Copy, Check, FileText, Video, Image, Presentation, Mic } from 'lucide-react';
import { toast } from 'sonner';

export const PromptGenerator = ({ project }) => {
  const [copiedIndex, setCopiedIndex] = useState(null);

  // Utility function to strip HTML tags and convert to plain text
  const stripHtml = (html) => {
    if (!html) return '';
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || '';
  };

  // Generate comprehensive project text with all data
  const generateProjectText = () => {
    let text = '';
    
    // Project title and description
    text += `Título del Proyecto: ${stripHtml(project.title)}\n\n`;
    text += `Descripción: ${stripHtml(project.description)}\n\n`;
    
    // Actions/Phases
    if (project.actions && project.actions.length > 0) {
      text += `Acciones/Fases (${project.actions.length}):\n\n`;
      project.actions.forEach((action, idx) => {
        text += `${idx + 1}. ${stripHtml(action.title)}\n`;
        text += `   ${stripHtml(action.description)}\n`;
        
        // Action images
        if (action.images && action.images.length > 0) {
          text += `   Imágenes:\n`;
          action.images.forEach((img, imgIdx) => {
            if (img.description) {
              text += `   - ${img.description}\n`;
            }
          });
        }
        
        // Action links
        if (action.links && action.links.length > 0) {
          text += `   Enlaces:\n`;
          action.links.forEach((link) => {
            text += `   - ${link.title}: ${link.url}\n`;
          });
        }
        text += `\n`;
      });
    }
    
    // Project images
    if (project.images && project.images.length > 0) {
      text += `Imágenes del Proyecto:\n`;
      project.images.forEach((img, idx) => {
        if (img.description) {
          text += `- ${img.description}\n`;
        }
      });
      text += `\n`;
    }
    
    // Project links
    if (project.links && project.links.length > 0) {
      text += `Enlaces Relacionados:\n`;
      project.links.forEach((link) => {
        text += `- ${link.title}: ${link.url}\n`;
      });
    }
    
    return text;
  };

  const prompts = [
    {
      id: 'text',
      icon: FileText,
      title: 'Prompt para Generación de Texto',
      description: 'Para ChatGPT, Claude, Gemini, etc.',
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      generate: () => {
        const projectText = generateProjectText();
        return `Act as a professional golf expert and communications specialist. Based on the following project information, generate a professional, clear, and engaging text that can be used for presentations, documents, or communications:

${projectText}

Please create a coherent and professional text that synthesizes this information in an attractive way for a professional golf audience.`;
      }
    },
    {
      id: 'video',
      icon: Video,
      title: 'Prompt para Generación de Video',
      description: 'Para Runway, Pika, Sora, etc.',
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      generate: () => {
        const projectText = generateProjectText();
        return `Act as a video director and scriptwriter specialized in professional sports content. Based on the following golf project, create a detailed script for a professional video:

${projectText}

Generate:
1. Video structure (introduction, development, conclusion)
2. Key scenes with visual descriptions
3. Narrative text or voiceover
4. Camera shot and angle suggestions
5. Recommended music or sound atmosphere
6. Approximate duration of each section`;
      }
    },
    {
      id: 'image',
      icon: Image,
      title: 'Prompt para Generación de Imágenes',
      description: 'Para DALL-E, Midjourney, Stable Diffusion, etc.',
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      generate: () => {
        const title = stripHtml(project.title);
        const description = stripHtml(project.description);
        return `Generate a high-quality professional image for a golf project with the following characteristics:

Title: ${title}
Context: ${description}

Image specifications:
- Style: Professional, elegant, sporty
- Environment: Golf course, premium atmosphere
- Colors: Natural greens, golds, earth tones
- Composition: Balanced and modern
- Lighting: Natural, warm, professional
- Quality: High resolution, 4K, photorealistic

The image should convey professionalism, excellence, and the essence of high-level golf.`;
      }
    },
    {
      id: 'presentation',
      icon: Presentation,
      title: 'Prompt para Creación de Presentación',
      description: 'Para crear slides o presentaciones',
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
      generate: () => {
        const projectText = generateProjectText();
        return `Act as a professional presentation designer specialized in sports. Based on the following golf project, create a complete presentation structure:

${projectText}

Generate:
1. Attractive title and subtitle for the cover
2. Slide structure (minimum 8-10 slides)
3. Specific content for each slide (titles, bullets, key texts)
4. Image/graphic suggestions for each slide
5. Presenter notes with key points
6. Conclusion/call-to-action slide

Style: Professional, modern, minimalist
Colors: Golf greens, golds, whites, navy blue`;
      }
    },
    {
      id: 'audio',
      icon: Mic,
      title: 'Prompt para Generación de Audio/Podcast',
      description: 'Para crear guiones de audio, podcasts, narración',
      color: 'text-red-600',
      bgColor: 'bg-red-50',
      generate: () => {
        const projectText = generateProjectText();
        return `Act as a podcast scriptwriter and professional announcer specialized in golf. Based on the following project, create a complete audio/podcast script:

${projectText}

Generate:
1. Engaging introduction (initial hook)
2. Presentation of the main topic
3. Content development in clear sections
4. Natural transitions between sections
5. Relevant examples or anecdotes
6. Memorable conclusion
7. Call to action or closing

Style: Conversational but professional, dynamic, approachable
Approximate duration: 5-10 minutes
Tone: Enthusiastic, expert, inspiring`;
      }
    }
  ];

  const copyToClipboard = async (text, index) => {
    try {
      // Modern approach
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      
      setCopiedIndex(index);
      toast.success('Prompt copiado al portapapeles');
      
      // Reset copied state after 2 seconds
      setTimeout(() => setCopiedIndex(null), 2000);
    } catch (err) {
      toast.error('Error al copiar el prompt');
      console.error('Copy failed:', err);
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-slate-800 mb-2">Generador de Prompts</h2>
        <p className="text-slate-600">
          Proyecto: <span className="font-semibold" dangerouslySetInnerHTML={{ __html: project.title }} />
        </p>
        <p className="text-sm text-slate-500 mt-2">
          Copia cualquiera de estos prompts y pégalo en tu herramienta de IA favorita
        </p>
      </div>

      <div className="grid gap-4">
        {prompts.map((prompt, index) => {
          const Icon = prompt.icon;
          const isCopied = copiedIndex === index;
          const generatedPrompt = prompt.generate();
          
          return (
            <Card key={prompt.id} className="overflow-hidden">
              <CardHeader className={`${prompt.bgColor} border-b`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <Icon className={`h-6 w-6 ${prompt.color} mt-1`} />
                    <div>
                      <CardTitle className="text-lg">{prompt.title}</CardTitle>
                      <CardDescription>{prompt.description}</CardDescription>
                    </div>
                  </div>
                  <Button
                    onClick={() => copyToClipboard(generatedPrompt, index)}
                    variant={isCopied ? "default" : "outline"}
                    size="sm"
                    className={isCopied ? "bg-green-600 hover:bg-green-700" : ""}
                  >
                    {isCopied ? (
                      <>
                        <Check className="mr-2 h-4 w-4" />
                        Copiado
                      </>
                    ) : (
                      <>
                        <Copy className="mr-2 h-4 w-4" />
                        Copiar
                      </>
                    )}
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="bg-slate-50 rounded-lg p-4 max-h-64 overflow-y-auto">
                  <pre className="text-sm text-slate-700 whitespace-pre-wrap font-sans">
                    {generatedPrompt}
                  </pre>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};
